import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface MineItem {
  id: string;
  name: string;
  operator: string | null;
  parent_company: string | null;
  state: string;
  mine_type: string;
  subscription_tier: string;
  status: string;
  eem_name: string | null;
  eem_contact: string | null;
  user_count: number;
  pending_count: number;
}

interface UserItem {
  id: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  approval_status: string;
  email_verified: boolean;
  requested_role: string | null;
  requested_org_name: string | null;
  site_approved_at: string | null;
  jmb_approved_at: string | null;
  rejected_reason: string | null;
  created_at: string | null;
  last_login: string | null;
}

type Tab = 'pending' | 'mines' | 'users';

const APPROVAL_COLORS: Record<string, { bg: string; text: string }> = {
  pending_site: { bg: '#fff3e0', text: '#e65100' },
  pending_jmb: { bg: '#e3f2fd', text: '#1565c0' },
  approved: { bg: '#e8f5e9', text: '#2e7d32' },
  rejected: { bg: '#ffebee', text: '#c62828' },
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active: { bg: '#e8f5e9', text: '#2e7d32' },
  onboarding: { bg: '#fff3e0', text: '#e65100' },
  suspended: { bg: '#ffebee', text: '#c62828' },
};

const AdminPanel: React.FC = () => {
  const [tab, setTab] = useState<Tab>('pending');
  const [mines, setMines] = useState<MineItem[]>([]);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [pendingUsers, setPendingUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<{ userId: string; name: string; type: 'site' | 'jmb' } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const navigate = useNavigate();

  const loggedUser = JSON.parse(localStorage.getItem('psf_user') || '{}');

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const loadMines = useCallback(async () => {
    try {
      const { data } = await api.get<MineItem[]>('/api/admin/mines');
      setMines(data);
    } catch (err) { console.error(err); }
  }, []);

  const loadUsers = useCallback(async (status?: string) => {
    try {
      const params: any = {};
      if (status) params.status = status;
      const { data } = await api.get<UserItem[]>('/api/admin/users', { params });
      if (status) {
        setPendingUsers(data);
      } else {
        setUsers(data);
      }
    } catch (err) { console.error(err); }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      loadMines(),
      loadUsers(),
      loadUsers('pending_jmb'),
    ]);
    // Also load pending_site
    try {
      const { data } = await api.get<UserItem[]>('/api/admin/users', { params: { status: 'pending_site' } });
      setPendingUsers(prev => [...data, ...prev.filter(u => u.approval_status !== 'pending_site')]);
    } catch {}
    setLoading(false);
  }, [loadMines, loadUsers]);

  useEffect(() => {
    if (loggedUser.role !== 'jmb_admin' && loggedUser.role !== 'eem' && loggedUser.role !== 'site_admin') {
      navigate('/');
      return;
    }
    loadAll();
  }, []);

  const handleApprove = async (userId: string, type: 'site' | 'jmb') => {
    setActionLoading(userId);
    try {
      const endpoint = type === 'site'
        ? `/api/admin/users/${userId}/site-approve`
        : `/api/admin/users/${userId}/jmb-approve`;
      await api.post(endpoint, { action: 'approve' });
      showNotification(type === 'jmb' ? 'User approved and activated!' : 'User approved — pending JMB review');
      await loadAll();
    } catch (err: any) {
      showNotification(err.response?.data?.detail || 'Approval failed', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(rejectModal.userId);
    try {
      const endpoint = rejectModal.type === 'site'
        ? `/api/admin/users/${rejectModal.userId}/site-approve`
        : `/api/admin/users/${rejectModal.userId}/jmb-approve`;
      await api.post(endpoint, { action: 'reject', reason: rejectReason || undefined });
      showNotification('User rejected');
      setRejectModal(null);
      setRejectReason('');
      await loadAll();
    } catch (err: any) {
      showNotification(err.response?.data?.detail || 'Rejection failed', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivateMine = async (mineId: string) => {
    // For now, we don't have a dedicated activate endpoint — this would update org status
    showNotification('Mine activation coming soon');
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const totalPending = pendingUsers.length;
  const pendingJmb = pendingUsers.filter(u => u.approval_status === 'pending_jmb').length;
  const pendingSite = pendingUsers.filter(u => u.approval_status === 'pending_site').length;
  const onboardingMines = mines.filter(m => m.status === 'onboarding').length;

  const handleLogout = () => {
    localStorage.removeItem('psf_token');
    localStorage.removeItem('psf_user');
    navigate('/login');
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <img src="/logo-white.png" alt="JMB Engineering" style={{ height: '38px' }} />
          <div style={{ width: '1px', height: '36px', background: 'rgba(255,255,255,0.2)' }} />
          <div>
            <h1 style={styles.headerTitle}>PSF Platform</h1>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '1px', letterSpacing: '0.5px' }}>Plant Safety File Platform</div>
          </div>
          <span style={styles.adminBadge}>Admin</span>
        </div>
        <div style={styles.headerRight}>
          <button onClick={() => navigate('/')} style={styles.navBtn}>← Dashboard</button>
          <span style={styles.userName}>{loggedUser.name}</span>
          <button onClick={handleLogout} style={styles.logoutBtn}>Logout</button>
        </div>
      </header>

      {/* Notification */}
      {notification && (
        <div style={{
          ...styles.notification,
          background: notification.type === 'success' ? '#4caf50' : '#f44336',
        }}>
          {notification.type === 'success' ? '✓' : '✗'} {notification.message}
        </div>
      )}

      {/* Stats Cards */}
      <div style={styles.statsRow}>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #ff9800' }}
             onClick={() => setTab('pending')}>
          <div style={styles.statNumber}>{totalPending}</div>
          <div style={styles.statLabel}>Pending Approvals</div>
          {pendingJmb > 0 && <div style={styles.statSub}>{pendingJmb} awaiting JMB</div>}
          {pendingSite > 0 && <div style={styles.statSub}>{pendingSite} awaiting site</div>}
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #2196f3' }}
             onClick={() => setTab('mines')}>
          <div style={styles.statNumber}>{mines.length}</div>
          <div style={styles.statLabel}>Registered Mines</div>
          {onboardingMines > 0 && <div style={styles.statSub}>{onboardingMines} onboarding</div>}
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #4caf50' }}
             onClick={() => setTab('users')}>
          <div style={styles.statNumber}>{users.length}</div>
          <div style={styles.statLabel}>Total Users</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {(['pending', 'mines', 'users'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              ...styles.tab,
              ...(tab === t ? styles.activeTab : {}),
            }}
          >
            {t === 'pending' && `Pending Approvals ${totalPending > 0 ? `(${totalPending})` : ''}`}
            {t === 'mines' && `Mines (${mines.length})`}
            {t === 'users' && `All Users (${users.length})`}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {loading ? (
          <div style={styles.loadingText}>Loading...</div>
        ) : (
          <>
            {/* ── Pending Approvals Tab ── */}
            {tab === 'pending' && (
              pendingUsers.length === 0 ? (
                <div style={styles.emptyState}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                  <h3 style={{ margin: '0 0 8px', color: '#333' }}>All caught up!</h3>
                  <p style={{ color: '#888', margin: 0 }}>No pending approval requests.</p>
                </div>
              ) : (
                <div style={styles.cardGrid}>
                  {pendingUsers.map(u => {
                    const colors = APPROVAL_COLORS[u.approval_status] || APPROVAL_COLORS.pending_site;
                    const isJmbPending = u.approval_status === 'pending_jmb';
                    const isSitePending = u.approval_status === 'pending_site';
                    return (
                      <div key={u.id} style={styles.userCard}>
                        <div style={styles.cardHeader}>
                          <div>
                            <div style={styles.cardName}>{u.name}</div>
                            <div style={styles.cardEmail}>{u.email}</div>
                          </div>
                          <span style={{ ...styles.approvalBadge, background: colors.bg, color: colors.text }}>
                            {u.approval_status === 'pending_site' ? 'Awaiting Site' : 'Awaiting JMB'}
                          </span>
                        </div>
                        <div style={styles.cardDetails}>
                          <div style={styles.cardRow}>
                            <span style={styles.cardLabel}>Mine:</span>
                            <span>{u.requested_org_name || '—'}</span>
                          </div>
                          <div style={styles.cardRow}>
                            <span style={styles.cardLabel}>Requested Role:</span>
                            <span style={styles.roleTag}>{(u.requested_role || 'site_user').replace(/_/g, ' ')}</span>
                          </div>
                          <div style={styles.cardRow}>
                            <span style={styles.cardLabel}>Submitted:</span>
                            <span>{formatDate(u.created_at)}</span>
                          </div>
                          {u.site_approved_at && (
                            <div style={styles.cardRow}>
                              <span style={styles.cardLabel}>Site Approved:</span>
                              <span style={{ color: '#2e7d32' }}>✓ {formatDate(u.site_approved_at)}</span>
                            </div>
                          )}
                        </div>
                        <div style={styles.cardActions}>
                          {isJmbPending && loggedUser.role === 'jmb_admin' && (
                            <>
                              <button
                                onClick={() => handleApprove(u.id, 'jmb')}
                                disabled={actionLoading === u.id}
                                style={styles.approveBtn}
                              >
                                {actionLoading === u.id ? '...' : '✓ Approve & Activate'}
                              </button>
                              <button
                                onClick={() => setRejectModal({ userId: u.id, name: u.name, type: 'jmb' })}
                                disabled={actionLoading === u.id}
                                style={styles.rejectBtn}
                              >
                                ✗ Reject
                              </button>
                            </>
                          )}
                          {isSitePending && (loggedUser.role === 'jmb_admin' || loggedUser.role === 'eem') && (
                            <>
                              <button
                                onClick={() => handleApprove(u.id, 'site')}
                                disabled={actionLoading === u.id}
                                style={styles.approveBtn}
                              >
                                {actionLoading === u.id ? '...' : '✓ Site Approve'}
                              </button>
                              <button
                                onClick={() => setRejectModal({ userId: u.id, name: u.name, type: 'site' })}
                                disabled={actionLoading === u.id}
                                style={styles.rejectBtn}
                              >
                                ✗ Reject
                              </button>
                            </>
                          )}
                          {isSitePending && loggedUser.role === 'jmb_admin' && (
                            <span style={styles.hintText}>Or wait for site admin email approval</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {/* ── Mines Tab ── */}
            {tab === 'mines' && (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Mine / Site</th>
                      <th style={styles.th}>Operator</th>
                      <th style={styles.th}>State</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Tier</th>
                      <th style={styles.th}>EEM</th>
                      <th style={styles.th}>Users</th>
                      <th style={styles.th}>Pending</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mines.map(m => {
                      const sc = STATUS_COLORS[m.status] || STATUS_COLORS.active;
                      return (
                        <tr key={m.id} style={styles.tr}>
                          <td style={{ ...styles.td, fontWeight: 600 }}>{m.name}</td>
                          <td style={styles.td}>{m.operator || '—'}</td>
                          <td style={styles.td}>{m.state}</td>
                          <td style={styles.td}><span style={styles.typeTag}>{m.mine_type.replace(/_/g, ' ')}</span></td>
                          <td style={styles.td}><span style={styles.typeTag}>{m.subscription_tier.replace(/_/g, ' ')}</span></td>
                          <td style={styles.td}>
                            <div style={{ fontSize: '13px' }}>{m.eem_name || '—'}</div>
                            {m.eem_contact && <div style={{ fontSize: '11px', color: '#888' }}>{m.eem_contact}</div>}
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>{m.user_count}</td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            {m.pending_count > 0 ? (
                              <span style={styles.pendingBadge}>{m.pending_count}</span>
                            ) : '—'}
                          </td>
                          <td style={styles.td}>
                            <span style={{ ...styles.statusBadge, background: sc.bg, color: sc.text }}>
                              {m.status}
                            </span>
                          </td>
                          <td style={styles.td}>
                            {m.status === 'onboarding' && (
                              <button onClick={() => handleActivateMine(m.id)} style={styles.smallBtn}>
                                Activate
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── All Users Tab ── */}
            {tab === 'users' && (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Name</th>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>Role</th>
                      <th style={styles.th}>Mine</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Email Verified</th>
                      <th style={styles.th}>Last Login</th>
                      <th style={styles.th}>Registered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => {
                      const ac = APPROVAL_COLORS[u.approval_status] || APPROVAL_COLORS.approved;
                      return (
                        <tr key={u.id} style={styles.tr}>
                          <td style={{ ...styles.td, fontWeight: 600 }}>{u.name}</td>
                          <td style={styles.td}>{u.email}</td>
                          <td style={styles.td}>
                            <span style={styles.roleTag}>{u.role.replace(/_/g, ' ')}</span>
                          </td>
                          <td style={styles.td}>{u.requested_org_name || '—'}</td>
                          <td style={styles.td}>
                            <span style={{ ...styles.statusBadge, background: ac.bg, color: ac.text }}>
                              {u.approval_status}
                            </span>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'center' }}>
                            {u.email_verified ? <span style={{ color: '#4caf50' }}>✓</span> : <span style={{ color: '#ccc' }}>✗</span>}
                          </td>
                          <td style={styles.td}>{formatDate(u.last_login)}</td>
                          <td style={styles.td}>{formatDate(u.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer style={styles.pageFooter}>
        © 2026 JMB Engineering Pty Ltd — Plant Safety File Platform
      </footer>

      {/* Reject Modal */}
      {rejectModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{ margin: '0 0 12px' }}>Reject {rejectModal.name}?</h3>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 16px' }}>
              This will deny their access request. You can provide a reason below.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (optional)"
              style={styles.textarea}
              rows={3}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => { setRejectModal(null); setRejectReason(''); }} style={styles.cancelBtn}>
                Cancel
              </button>
              <button onClick={handleReject} disabled={actionLoading === rejectModal.userId} style={styles.rejectBtn}>
                {actionLoading === rejectModal.userId ? 'Rejecting...' : 'Reject User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', background: '#f5f6fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', display: 'flex', flexDirection: 'column' },
  pageFooter: { marginTop: 'auto', padding: '16px 32px', background: '#1a1a2e', color: 'rgba(255,255,255,0.4)', fontSize: '12px', textAlign: 'center' as const, letterSpacing: '0.5px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 32px', background: '#1a1a2e', color: '#fff' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  headerTitle: { margin: 0, fontSize: '20px', fontWeight: 600 },
  adminBadge: { padding: '2px 10px', borderRadius: '12px', background: '#ff9800', color: '#fff', fontSize: '11px', fontWeight: 600 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  navBtn: { padding: '6px 16px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  userName: { fontWeight: 600, fontSize: '14px' },
  logoutBtn: { padding: '6px 16px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },

  notification: { padding: '12px 32px', color: '#fff', fontSize: '14px', fontWeight: 500, textAlign: 'center' as const },

  statsRow: { display: 'flex', gap: '20px', padding: '24px 32px' },
  statCard: { flex: 1, background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', cursor: 'pointer', transition: 'box-shadow 0.15s' },
  statNumber: { fontSize: '32px', fontWeight: 700, color: '#1a1a2e' },
  statLabel: { fontSize: '13px', color: '#666', marginTop: '4px', fontWeight: 600 },
  statSub: { fontSize: '12px', color: '#999', marginTop: '4px' },

  tabs: { display: 'flex', gap: '0', padding: '0 32px', borderBottom: '2px solid #e0e0e0' },
  tab: { padding: '12px 24px', border: 'none', background: 'transparent', fontSize: '14px', fontWeight: 500, color: '#888', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: '-2px', transition: 'all 0.15s' },
  activeTab: { color: '#1a1a2e', borderBottomColor: '#1a1a2e', fontWeight: 600 },

  content: { padding: '24px 32px' },
  loadingText: { padding: '48px', textAlign: 'center' as const, color: '#999' },
  emptyState: { padding: '64px', textAlign: 'center' as const },

  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '16px' },
  userCard: { background: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 20px', borderBottom: '1px solid #f0f0f0' },
  cardName: { fontSize: '16px', fontWeight: 600, color: '#1a1a2e' },
  cardEmail: { fontSize: '13px', color: '#888', marginTop: '2px' },
  cardDetails: { padding: '12px 20px' },
  cardRow: { display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#555' },
  cardLabel: { fontWeight: 600, color: '#999', fontSize: '12px', textTransform: 'uppercase' as const },
  cardActions: { display: 'flex', gap: '8px', padding: '12px 20px', borderTop: '1px solid #f0f0f0', alignItems: 'center', flexWrap: 'wrap' as const },

  approvalBadge: { padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' as const },
  roleTag: { fontSize: '12px', fontWeight: 600, color: '#555', textTransform: 'capitalize' as const },

  approveBtn: { padding: '8px 20px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  rejectBtn: { padding: '8px 20px', background: '#f44336', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 },
  cancelBtn: { padding: '8px 20px', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  smallBtn: { padding: '6px 14px', background: '#2196f3', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 },
  hintText: { fontSize: '11px', color: '#999', fontStyle: 'italic' as const },

  tableWrap: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  th: { padding: '12px 16px', textAlign: 'left' as const, fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase' as const, borderBottom: '2px solid #e0e0e0', background: '#fafafa', whiteSpace: 'nowrap' as const },
  tr: { borderBottom: '1px solid #f0f0f0', transition: 'background 0.15s' },
  td: { padding: '12px 16px', fontSize: '13px', color: '#333' },
  statusBadge: { padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize' as const, whiteSpace: 'nowrap' as const },
  pendingBadge: { display: 'inline-block', minWidth: '20px', padding: '2px 8px', borderRadius: '10px', background: '#ff9800', color: '#fff', fontSize: '11px', fontWeight: 700, textAlign: 'center' as const },
  typeTag: { fontSize: '12px', color: '#555', textTransform: 'capitalize' as const },

  modalOverlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '440px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  textarea: { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', resize: 'vertical' as const, fontFamily: 'inherit', boxSizing: 'border-box' as const },
};

export default AdminPanel;
